import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Descriptions as AntdDescriptions, Card, Tag, Divider } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import Descriptions from "../../components/general/antd/descriptions";
import SkipHistoryTable from "../../components/skip-analytics/skip-history-table";
import SkipAnalyticsFilter from "../../components/skip-analytics/skip-analytics-filter";
import SkipAnalyticsContext from "../../context/skip-analytics/context";
import SkipAnalyticsContextProvider from "../../context/skip-analytics/provider";
import AppContext from "../../context/app/context";
import styles from "./styles.module.scss";

const DriverHistoryPageContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const driverId = Number(id);
  const { direction } = useContext(AppContext);
  const { actions, historyQuery, driversStats } = useContext(SkipAnalyticsContext);

  // Get driver info from driversStats if available
  const driverInfo = driversStats?.data?.find((d) => d.driverId === driverId);

  // Fetch data when driverId, historyQuery, or direction changes
  useEffect(() => {
    if (driverId) {
      actions.getDriverSkipHistory(driverId);
      // Also get drivers stats to show driver info
      if (!driversStats) {
        actions.getDriversSkipStats();
      }
    }
  }, [driverId, historyQuery, direction]);

  return (
    <div className={styles.container}>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/skip-analytics")}
          style={{ paddingLeft: 0 }}
        >
          {t("back_to_analytics")}
        </Button>
      </div>

      {driverInfo && (
        <Card style={{ marginBottom: 16 }}>
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }}>
            <AntdDescriptions.Item label={t("driver_name")}>
              {driverInfo.driverName}
            </AntdDescriptions.Item>
            <AntdDescriptions.Item label={t("phone")}>
              {driverInfo.driverPhone || "-"}
            </AntdDescriptions.Item>
            <AntdDescriptions.Item label={t("total_skips")}>
              <Tag
                color={
                  driverInfo.totalSkips > 10
                    ? "red"
                    : driverInfo.totalSkips > 5
                    ? "orange"
                    : "green"
                }
              >
                {driverInfo.totalSkips}
              </Tag>
            </AntdDescriptions.Item>
            <AntdDescriptions.Item label={t("store_skips")}>
              <Tag color="blue">{driverInfo.storeSkips}</Tag>
            </AntdDescriptions.Item>
            <AntdDescriptions.Item label={t("order_skips")}>
              <Tag color="orange">{driverInfo.orderSkips}</Tag>
            </AntdDescriptions.Item>
            <AntdDescriptions.Item label={t("total_trips")}>
              {driverInfo.totalTrips}
            </AntdDescriptions.Item>
            <AntdDescriptions.Item label={t("avg_skips_per_trip")}>
              {driverInfo.avgSkipsPerTrip?.toFixed(2) || "0.00"}
            </AntdDescriptions.Item>
            <AntdDescriptions.Item label={t("last_skip_date")}>
              {driverInfo.lastSkipDate
                ? new Date(driverInfo.lastSkipDate).toLocaleDateString()
                : "-"}
            </AntdDescriptions.Item>
          </Descriptions>
        </Card>
      )}

      <SkipAnalyticsFilter />

      <SkipHistoryTable
        driverId={driverId}
        title={`${t("skip_history")} - ${driverInfo?.driverName || t("driver")} #${driverId}`}
      />
    </div>
  );
};

const DriverHistoryPage = () => {
  return (
    <SkipAnalyticsContextProvider>
      <DriverHistoryPageContent />
    </SkipAnalyticsContextProvider>
  );
};

export default DriverHistoryPage;
